import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Users, Calendar, Image, Layers, Settings } from 'lucide-react'
import { Project } from '../../types'

interface ProjectCardProps {
  project: Project
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate()
  const [showMenu, setShowMenu] = useState(false)

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(!showMenu)
  }

  const getTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays} days ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
      return `${Math.floor(diffDays / 365)} years ago`
    } catch {
      return 'Recently'
    }
  }

  return (
    <motion.div
      className="project-card relative group"
      onClick={() => navigate(`/project/${project.id}`)}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      {/* Hero Image */}
      <div className="relative w-full h-32 mb-4 rounded-lg overflow-hidden bg-gradient-to-br from-primary-400/20 to-primary-600/20">
        {project.heroImageUrl ? (
          <img
            src={project.heroImageUrl}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Layers className="w-12 h-12 text-white/30" />
          </div>
        )}
        
        {/* Overlay with aspect ratio indicator */}
        <div className="absolute top-2 left-2">
          <span className="glass-button text-xs px-2 py-1 text-white/90">
            {project.aspectRatio}
          </span>
        </div>

        {/* Menu button */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handleMenuClick}
            className="glass-button p-1.5 hover:bg-white/30"
          >
            <MoreVertical className="w-4 h-4 text-white" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <motion.div
              className="absolute right-0 top-full mt-1 w-40 glass-card border border-white/20 z-10"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
            >
              <div className="py-1">
                <button className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2">
                  <Settings className="w-3 h-3" />
                  <span>Settings</span>
                </button>
                <button className="w-full px-3 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center space-x-2">
                  <Users className="w-3 h-3" />
                  <span>Share</span>
                </button>
                <hr className="border-white/10 my-1" />
                <button className="w-full px-3 py-2 text-left text-sm text-red-300 hover:bg-red-500/20">
                  Delete
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Project Info */}
      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-white mb-1 line-clamp-1">
            {project.title}
          </h3>
          <p className="text-white/70 text-sm line-clamp-2">
            {project.description || 'No description'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center space-x-1 text-white/60">
            <Layers className="w-3 h-3" />
            <span>{project.totalScenes} scenes</span>
          </div>
          <div className="flex items-center space-x-1 text-white/60">
            <Image className="w-3 h-3" />
            <span>{project.totalImages} images</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="flex items-center space-x-1">
            {project.sharedWith.length > 0 && (
              <>
                <Users className="w-3 h-3 text-white/60" />
                <span className="text-xs text-white/60">
                  {project.sharedWith.length} collaborator{project.sharedWith.length !== 1 ? 's' : ''}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center space-x-1 text-xs text-white/60">
            <Calendar className="w-3 h-3" />
            <span>{getTimeAgo(project.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Close menu when clicking outside */}
      {showMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={(e) => {
            e.stopPropagation()
            setShowMenu(false)
          }}
        />
      )}
    </motion.div>
  )
}

export default ProjectCard 